/**
 * 🔑 PASSWORD RESET MODAL
 * 
 * Modal de verificação de código para reset de senha
 * Design: Consistente com TwoFactorModal (bonito, moderno, gradientes)
 * 
 * Features:
 * - Input de 6 dígitos
 * - Countdown de expiração (10 minutos)
 * - Botão "Acessar" que se acende quando 6 dígitos são digitados
 * - Botão "Reenviar código" quando tempo acaba
 * - Feedback visual (sucesso/erro)
 * - Tentativas restantes
 * 
 * @component
 */

import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import TwoFactorInput from '../auth/TwoFactorInput';
import './TwoFactorModal.scss'; // Reutilizar estilos do 2FA

const PasswordResetModal = ({ 
  show, 
  onHide, 
  onVerify, 
  onResend,
  email,
  expiresIn = 600 // 10 minutos em segundos
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [timeLeft, setTimeLeft] = useState(expiresIn);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  
  const intervalRef = useRef(null);

  // Countdown timer
  useEffect(() => {
    if (!show) return;

    setTimeLeft(expiresIn);
    setCanResend(false);

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [show, expiresIn]);

  /**
   * Formatar tempo restante (MM:SS)
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Handler de verificação de código
   */
  const handleVerify = async (submittedCode) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const result = await onVerify(submittedCode || code);

      if (result.success) {
        setSuccess(true);
        setError('');
        
        // Aguardar 800ms para mostrar feedback visual
        // O componente pai já mudou para etapa 3 em handleVerifyCode
        // Não precisamos chamar onHide aqui, apenas resetar o estado do modal
        setTimeout(() => {
          // Resetar estado do modal (mas não chamar onHide para não interferir)
          setCode('');
          setSuccess(false);
          setError('');
          setAttemptsLeft(3);
          // Fechar modal silenciosamente apenas para limpar visual
          // Não chamar onHide porque o pai já controla a navegação
        }, 800);
      } else {
        setError(result.message || 'Código inválido');
        setSuccess(false);
        
        // Atualizar tentativas restantes
        if (result.attemptsLeft !== undefined) {
          setAttemptsLeft(result.attemptsLeft);
        }
        
        // Limpar código após erro
        setCode('');
      }
    } catch (err) {
      setError(err.message || 'Erro ao verificar código');
      setSuccess(false);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handler de reenvio de código
   */
  const handleResend = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);
    setCode('');

    try {
      const result = await onResend();

      if (result.success) {
        // Resetar timer
        setTimeLeft(result.expiresIn || expiresIn);
        setCanResend(false);
        setAttemptsLeft(3);
        
        // Reiniciar countdown
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        intervalRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(intervalRef.current);
              setCanResend(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(result.message || 'Erro ao reenviar código');
      }
    } catch (err) {
      setError(err.message || 'Erro ao reenviar código');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mascara email para exibição segura
   */
  const maskEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) {
      return `${localPart[0]}***@${domain}`;
    }
    return `${localPart[0]}***${localPart[localPart.length - 1]}@${domain}`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="two-factor-modal"
    >
      <div className="two-factor-modal__container">
        {/* Header com gradiente */}
        <div className="two-factor-modal__header">
          <div className="two-factor-modal__icon">
            <Icon icon="mdi:key-reset" />
          </div>
          <h3 className="two-factor-modal__title">Código de Verificação</h3>
          <p className="two-factor-modal__subtitle">
            Digite o código enviado para<br />
            <strong>{maskEmail(email)}</strong>
          </p>
        </div>

        {/* Corpo */}
        <div className="two-factor-modal__body">
          {/* Input de 6 dígitos */}
          <TwoFactorInput
            value={code}
            onChange={setCode}
            error={!!error}
            disabled={loading || success}
          />

          {/* Countdown */}
          {!canResend && timeLeft > 0 && (
            <div className="two-factor-modal__countdown">
              <Icon icon="mdi:clock-outline" />
              <span>Código expira em <strong>{formatTime(timeLeft)}</strong></span>
            </div>
          )}

          {/* Tentativas restantes */}
          {attemptsLeft < 3 && attemptsLeft > 0 && !success && (
            <div className="two-factor-modal__attempts">
              <Icon icon="mdi:alert-circle-outline" />
              <span>{attemptsLeft} tentativa(s) restante(s)</span>
            </div>
          )}

          {/* Feedback de sucesso */}
          {success && (
            <div className="two-factor-modal__success">
              <Icon icon="mdi:check-circle" />
              <span>Código validado com sucesso!</span>
            </div>
          )}

          {/* Feedback de erro */}
          {error && (
            <div className="two-factor-modal__error">
              <Icon icon="mdi:alert-circle" />
              <span>{error}</span>
            </div>
          )}

          {/* Ação principal / Reenvio */}
          {canResend ? (
            <button
              onClick={handleResend}
              disabled={loading}
              className="two-factor-modal__resend"
            >
              <Icon icon="mdi:email-sync" />
              Reenviar código
            </button>
          ) : (
            <button
              onClick={() => handleVerify(code)}
              disabled={loading || !/^\d{6}$/.test(code)}
              className="two-factor-modal__resend"
            >
              <Icon icon="mdi:arrow-right" />
              Continuar
            </button>
          )}

          {/* Link voltar */}
          <button
            onClick={onHide}
            className="two-factor-modal__back"
            disabled={loading}
          >
            <Icon icon="mdi:arrow-left" />
            Voltar
          </button>

          {/* Aviso sobre delay - Footer */}
          <div className="two-factor-modal__info-box">
            <Icon icon="mdi:information-outline" width="16" />
            <span>O email pode levar até 2 minutos para chegar. Caso não chegue, verifique sua caixa de spam.</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PasswordResetModal;

